import { Box } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { CreateVoice, EditVoice } from 'features/voice';

import { countryModel } from 'entities/country';
import { PublishedFilter, VoicesFilters, VoicesTable, voiceLib, voiceModel } from 'entities/voice';

import { VOICE_SORT_FIELDS, VoiceSortField, VoiceStatus, VoiceTarget, Voices } from 'shared/api';
import { useDebouncedSearch } from 'shared/lib';
import { ConfirmDialog, LoadingProgress } from 'shared/ui';

// Старые закладки могут нести ?sortBy=bio_en — сервер такое значение отвергает с 400.
// Функция объявлена вне компонента: внутри она пересоздавалась бы каждый рендер
// и попадала бы в зависимости useCallback.
const isVoiceSortField = (value: string | null): value is VoiceSortField =>
  !!value && (VOICE_SORT_FIELDS as readonly string[]).includes(value);

// Мусор в ?status= / ?isPublished= из закладки сервер тоже отвергает с 400 — отбрасываем его здесь.
const parsePublishedFilter = (value: string | null): PublishedFilter =>
  value === 'true' || value === 'false' ? value : '';

export const VoicesPage = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filter, setFilter] = useState(searchParams.get('filter') ?? '');
  const [target, setTarget] = useState<VoiceTarget | ''>((searchParams.get('target') as VoiceTarget | null) ?? '');
  const [status, setStatus] = useState<VoiceStatus | ''>(() => {
    const fromUrl = searchParams.get('status');
    return voiceLib.isVoiceStatus(fromUrl) ? fromUrl : '';
  });
  const [published, setPublished] = useState<PublishedFilter>(() =>
    parsePublishedFilter(searchParams.get('isPublished')),
  );
  const [sortBy, setSortBy] = useState<VoiceSortField>(() => {
    const fromUrl = searchParams.get('sortBy');
    return isVoiceSortField(fromUrl) ? fromUrl : 'name';
  });
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(
    (searchParams.get('sortOrder') as 'asc' | 'desc') ?? 'asc',
  );
  const [page, setPage] = useState(parseInt(searchParams.get('page') ?? '1', 10));
  const [rowsPerPage, setRowsPerPage] = useState(parseInt(searchParams.get('limit') ?? '25', 10));

  const [editVoice, setEditVoice] = useState<Voices | null>(null);
  const [deleteVoice, setDeleteVoice] = useState<Voices | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);

  const { data: countriesData } = countryModel.useAllCountries();
  const countries = countriesData?.data ?? [];

  const { mutate: createVoice, isLoading: isCreating, isSuccess: isCreateSuccess } = voiceModel.createVoiceAsync();
  const { mutate: updateVoice, isLoading: isUpdating, isSuccess: isUpdateSuccess } = voiceModel.updateVoiceAsync();
  const { mutate: deleteVoiceMutation, isLoading: isDeleting } = voiceModel.deleteVoiceAsync();

  const debouncedFilter = useDebouncedSearch(filter, 250);

  const params = useMemo(
    () => ({
      filter: debouncedFilter || undefined,
      target: target || undefined,
      status: status || undefined,
      // Не `published || undefined`: false — валидный фильтр (неопубликованные).
      isPublished: published === '' ? undefined : published === 'true',
      sortBy,
      sortOrder,
      page,
      limit: rowsPerPage,
    }),
    [debouncedFilter, target, status, published, sortBy, sortOrder, page, rowsPerPage],
  );

  const { isLoading, isFetching } = voiceModel.getVoicesAsync(params)(dispatch);
  const { voiceList, pagination } = voiceModel.useVoice();

  useEffect(() => {
    const newParams: Record<string, string> = {
      page: page.toString(),
      limit: rowsPerPage.toString(),
      sortBy,
      sortOrder,
    };

    if (debouncedFilter) newParams.filter = debouncedFilter;
    if (target) newParams.target = target;
    if (status) newParams.status = status;
    if (published) newParams.isPublished = published;

    setSearchParams(newParams, { replace: true });
  }, [debouncedFilter, target, status, published, sortBy, sortOrder, page, rowsPerPage, setSearchParams]);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback((newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  }, []);

  const handleFilterChange = useCallback((value: string) => {
    setFilter(value);
    setPage(1);
  }, []);

  const handleTargetChange = useCallback((value: VoiceTarget | '') => {
    setTarget(value);
    setPage(1);
  }, []);

  const handleStatusChange = useCallback((value: VoiceStatus | '') => {
    setStatus(value);
    setPage(1);
  }, []);

  const handlePublishedChange = useCallback((value: PublishedFilter) => {
    setPublished(value);
    setPage(1);
  }, []);

  const handleSortChange = useCallback(
    (columnId: string) => {
      if (!isVoiceSortField(columnId)) return;
      if (sortBy === columnId) {
        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
      } else {
        setSortBy(columnId);
        setSortOrder('asc');
      }
      setPage(1);
    },
    [sortBy, sortOrder],
  );

  const handleCreateVoice = useCallback(
    (data: Partial<Voices>) => {
      createVoice(data as Omit<Voices, '_id'>);
    },
    [createVoice],
  );

  const handleEditVoice = useCallback((voice: Voices) => {
    setEditVoice(voice);
    setIsEditOpen(true);
  }, []);

  const handleUpdateVoice = useCallback(
    (data: Partial<Voices>) => {
      if (editVoice) {
        updateVoice({ id: editVoice._id, data });
      }
    },
    [editVoice, updateVoice],
  );

  const handleDeleteVoice = useCallback((voice: Voices) => {
    setDeleteVoice(voice);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (deleteVoice) {
      deleteVoiceMutation(deleteVoice._id);
      setDeleteVoice(null);
    }
  }, [deleteVoice, deleteVoiceMutation]);

  const handleCancelDelete = useCallback(() => {
    setDeleteVoice(null);
  }, []);

  const handleCloseEdit = useCallback(() => {
    setIsEditOpen(false);
    setEditVoice(null);
  }, []);

  if (isLoading && !voiceList.length) return <LoadingProgress />;

  return (
    <Box display="flex" flexDirection="column" px={3} py={2}>
      <VoicesFilters
        filter={filter}
        target={target}
        status={status}
        published={published}
        totalCount={pagination?.total ?? 0}
        onFilterChange={handleFilterChange}
        onTargetChange={handleTargetChange}
        onStatusChange={handleStatusChange}
        onPublishedChange={handlePublishedChange}
        addButton={
          <CreateVoice
            onSubmit={handleCreateVoice}
            isSuccess={isCreateSuccess}
            isLoading={isCreating}
            countries={countries}
          />
        }
      />

      <VoicesTable
        voices={voiceList || []}
        pagination={pagination}
        page={page}
        rowsPerPage={rowsPerPage}
        sortBy={sortBy}
        sortOrder={sortOrder}
        isLoading={isFetching}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        onSortChange={handleSortChange}
        onEdit={handleEditVoice}
        onDelete={handleDeleteVoice}
      />

      <EditVoice
        voice={editVoice}
        isOpen={isEditOpen}
        onClose={handleCloseEdit}
        onSubmit={handleUpdateVoice}
        isSuccess={isUpdateSuccess}
        isLoading={isUpdating}
        countries={countries}
      />

      <ConfirmDialog
        open={!!deleteVoice}
        title="Delete Voice"
        message={`Are you sure you want to delete the voice "${
          deleteVoice?.name ?? ''
        }"? This action cannot be undone.`}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isLoading={isDeleting}
      />
    </Box>
  );
};
